// The band below which a parent or guardian holds the sign-in (#169, Martin's
// credential-handover decision of 2026-08-07). The account row is the
// player's either side of this line; only who holds the password changes.
// Exported for the admin console's set-DOB dialog (#569), which must show
// the same guardian note at the same threshold — two copies of this number
// is how the form and the admin surface would come to disagree about who
// holds a sign-in.
export const GUARDIAN_HELD_BELOW_AGE = 11;

/**
 * Today's CIVIL date in New Zealand, as `YYYY-MM-DD`.
 *
 * Not UTC, and not the browser's local zone. This must agree exactly with
 * `todayInNewZealand` in `api/src/lib/dateOfBirth.ts`: if the form and the
 * API disagree about what day it is, the form routes a child down one path
 * and the API judges them by another. UTC is the wrong anchor because New
 * Zealand runs UTC+12/+13 — for the 12-13 hours between local midnight and
 * UTC midnight, today in Auckland is tomorrow by UTC's reckoning, which is
 * how a newborn's date of birth came to be rejected as "in the future".
 * `Intl` carries the IANA database, so DST resolves itself; never hardcode
 * an offset.
 */
export function nzToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Whole years between an ISO `YYYY-MM-DD` date of birth and today in New
 * Zealand, or null if the string isn't a usable past date. Deliberately
 * calendar-based rather than milliseconds/365.25 — an eleventh birthday is
 * the threshold the guardian model turns on, so being a day out around leap
 * years or a timezone boundary is not acceptable.
 */
export function ageFromDob(dob, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || "")) return null;
  const [y, m, d] = dob.split("-").map(Number);
  const born = new Date(Date.UTC(y, m - 1, d));
  // Round-trip check rejects "2026-02-30" and friends, which `Date.UTC`
  // silently rolls forward into March.
  if (born.getUTCFullYear() !== y || born.getUTCMonth() !== m - 1 || born.getUTCDate() !== d) return null;

  // Both sides are now `YYYY-MM-DD`, so "is it in the future" is a plain
  // string comparison and no timezone arithmetic is involved at all — which
  // is exactly what went wrong when this compared Date objects.
  const today = nzToday(now);
  if (dob > today) return null;
  const [ty, tm, td] = today.split("-").map(Number);
  let age = ty - y;
  const hadBirthday = tm > m || (tm === m && td >= d);
  if (!hadBirthday) age -= 1;
  return age;
}
